import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

type AiEditAction = 'improve_clarity' | 'make_more_formal' | 'shorten' | 'check_sources';

interface AiEditModalProps {
  isOpen: boolean;
  action: AiEditAction | null;
  sessionId: string;
  documentContent: string;
  isSelectionMode: boolean;
  onAccept: (revisedContent: string) => void;
  onClose: () => void;
}

const ACTION_LABELS: Record<AiEditAction, string> = {
  improve_clarity: 'Improve Clarity',
  make_more_formal: 'Make More Formal',
  shorten: 'Shorten This',
  check_sources: 'Check Against Sources',
};

export function AiEditModal({
  isOpen,
  action,
  sessionId,
  documentContent,
  isSelectionMode,
  onAccept,
  onClose,
}: AiEditModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !action) return;

    setIsLoading(true);
    setResult(null);
    setError(null);

    supabase.functions
      .invoke('composer-ai-edit', {
        body: {
          session_id: sessionId,
          document_content: documentContent,
          action,
        },
      })
      .then(({ data, error: fnError }) => {
        if (fnError || !data?.revised_content) {
          setError('Something went wrong. Please try again.');
        } else {
          setResult(data.revised_content);
        }
      })
      .catch(() => {
        setError('Something went wrong. Please try again.');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [isOpen, action, sessionId, documentContent]);

  const isCheckSources = action === 'check_sources';
  const title = action ? ACTION_LABELS[action] : '';

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        className={isCheckSources ? 'sm:max-w-xl' : 'sm:max-w-4xl'}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <Badge variant="secondary" className="w-fit text-xs font-normal bg-muted text-muted-foreground">
            {isSelectionMode ? 'Editing selected text only' : 'Editing full document'}
          </Badge>
        </DialogHeader>

        {/* Loading */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Vera is reviewing your document...</p>
          </div>
        )}

        {/* Error */}
        {!isLoading && error && (
          <>
            <div className="flex items-center justify-center py-12">
              <p className="text-sm text-destructive">{error}</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Close</Button>
            </DialogFooter>
          </>
        )}

        {/* Check Sources result */}
        {!isLoading && !error && result && isCheckSources && (
          <>
            <div>
              <p className="text-xs text-muted-foreground mb-2">Vera's findings:</p>
              <div className="max-h-96 overflow-y-auto rounded border border-border bg-muted p-4">
                <p className="text-sm whitespace-pre-wrap text-foreground">{result}</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Close</Button>
            </DialogFooter>
          </>
        )}

        {/* Diff view for rewrite actions */}
        {!isLoading && !error && result && !isCheckSources && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Original</p>
                <div
                  className="max-h-96 overflow-y-auto rounded border border-border bg-muted p-4 text-sm"
                  dangerouslySetInnerHTML={{ __html: documentContent }}
                />
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Revised</p>
                <div
                  className="max-h-96 overflow-y-auto rounded border border-border bg-background p-4 text-sm"
                  dangerouslySetInnerHTML={{ __html: result }}
                />
              </div>
            </div>
            <DialogFooter className="flex-col items-end gap-2">
              <div className="flex gap-2">
                <Button variant="ghost" onClick={onClose}>Reject</Button>
                <Button onClick={() => onAccept(result)}>Accept Changes</Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Review all AI suggestions before accepting. You are responsible for final policy content.
              </p>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
